/**
 * Project-wide date format: YYYY-MM-DD (display and API), yyyy/MM/dd also supported for parsing.
 * Use these for all user-facing date display and date inputs.
 */

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

/**
 * Format as YYYY-MM-DD for use in date input fields (e.g. type="date" value).
 * Returns "" when value is null/undefined or invalid.
 */
export function formatDateToInputDDMMYYYY(
  value: Date | string | null | undefined,
): string {
  if (value == null || value === "") return "";
  try {
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch {
    return "";
  }
}

/**
 * Parse a YYYY-MM-DD or yyyy/MM/dd string to a Date (noon local).
 * Returns null if invalid or empty.
 */
export function parseDDMMYYYY(s: string): Date | null {
  const trimmed = (s ?? "").trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/[/-]/).map((p) => p.trim());
  if (parts.length !== 3) return null;
  const first = parseInt(parts[0], 10);
  const second = parseInt(parts[1], 10);
  const third = parseInt(parts[2], 10);
  if (Number.isNaN(first) || Number.isNaN(second) || Number.isNaN(third))
    return null;
  let year: number, month: number, day: number;
  if (parts[0].length === 4) {
    year = first;
    month = second - 1;
    day = third;
  } else {
    day = first;
    month = second - 1;
    year = third;
  }
  const d = new Date(year, month, day);
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day)
    return null;
  return d;
}

/**
 * Format a Date to YYYY-MM-DD using local date (no UTC conversion).
 * Use this when sending date-only values to the API to avoid timezone shift
 * (e.g. 2026-06-25 in India stays 2026-06-25, not 2026-06-24).
 */
export function toISODateString(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Format Date to YYYY-MM-DD (local) for API. Returns "" for null/undefined.
 * Prefer this over date.toISOString().split("T")[0] to avoid timezone shift.
 */
export function dateToLocalYYYYMMDD(value: Date | null | undefined): string {
  if (value == null) return "";
  return toISODateString(value);
}

/**
 * Format as YYYY-MM-DD.
 * @param fallback - Returned when value is null/undefined or invalid.
 */
export function formatDateDDMMYYYY(
  value: Date | string | null | undefined,
  fallback = "Not set",
): string {
  if (value == null) return fallback;
  try {
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return fallback;
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    return `${year}-${month}-${day}`;
  } catch {
    return fallback;
  }
}

/**
 * Format as YYYY-MM-DD, HH:MM am/pm.
 * @param fallback - Returned when value is null/undefined or invalid.
 */
export function formatDateTimeDDMMYYYY(
  value: Date | string | null | undefined,
  fallback = "Not set",
): string {
  if (value == null) return fallback;
  try {
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return fallback;
    const datePart = formatDateDDMMYYYY(d, "");
    const timePart = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart}, ${timePart}`;
  } catch {
    return fallback;
  }
}
